import type { ConsignmentLineItem, DerivedPallet } from '@/entities/consignments';

import { calculateTotals } from './calculate-units';

/** Tolerance so an exact fit isn't rejected by floating-point rounding. */
const EPSILON = 1e-6;

export interface PalletCapacityCheck {
  ok: boolean;
  /** Which dimension failed (only set when `ok` is false). */
  dimension?: 'weight' | 'volume';
  /** Resulting load on the dimension that failed. */
  resulting?: number;
  /** The cap that would be exceeded. */
  max?: number;
}

/**
 * Checks whether placing `addedLines` on `pallet` keeps it within its weight and
 * volume capacity. A `null`/`undefined` cap means "no limit" for that dimension.
 *
 * Mirrors the backend rule (`PalletCapacity.assertWithinCapacity`) — the backend
 * remains the source of truth; this is a UX guard to avoid a doomed request.
 *
 * `addedLines` must contain ONLY the lines being newly placed on the pallet
 * (exclude any already assigned to it), so the pallet's existing
 * `currentWeight` / `currentVolume` are not double-counted.
 */
export function checkPalletCapacity(
  pallet: Pick<DerivedPallet, 'maxWeight' | 'maxVolume' | 'currentWeight' | 'currentVolume'>,
  addedLines: ConsignmentLineItem[]
): PalletCapacityCheck {
  const added = calculateTotals(addedLines);
  const resultingWeight = (pallet.currentWeight ?? 0) + added.totalWeight;
  const resultingVolume = (pallet.currentVolume ?? 0) + added.totalVolume;

  if (pallet.maxWeight != null && resultingWeight > pallet.maxWeight + EPSILON) {
    return { ok: false, dimension: 'weight', resulting: resultingWeight, max: pallet.maxWeight };
  }
  if (pallet.maxVolume != null && resultingVolume > pallet.maxVolume + EPSILON) {
    return { ok: false, dimension: 'volume', resulting: resultingVolume, max: pallet.maxVolume };
  }
  return { ok: true };
}
